import React, {useEffect, useState} from 'react'
import {StatusMap, TObservablePage} from '../../types/commerce'
import {flowResult} from 'mobx'
import {observer} from 'mobx-react-lite'
import {getServerState} from 'react-instantsearch-hooks-server'
import {useBasketStore, useCategoryStore, useContentStore, useGlobalStore, useOrderStore} from '../../store/hooks/useStore'
import {Layout} from '../../components/layout'
import {Box, Container, Stack} from '../../vanilla'
import PageNotFound from '../../pages/page-not-found'
import CategoryPage from '../category'
import {renderToString} from 'react-dom/server'
//@ts-ignore
import {InstantSearch as AlgoliaInstantSearch, Pagination} from 'react-instantsearch-hooks-web'
import {Seo} from '../../components/seo'
import InstantSearch, {Results, ResultsHeader} from '../../components/algolia/instantsearch'
import {Configure} from 'react-instantsearch-hooks-web'
import {CommerceAPIProvider, StoreProvider} from '../../contexts'
// @ts-ignore route conflict
import {StaticRouter} from 'react-router-dom'
import {IntlProvider} from 'react-intl'
import algoliasearch from 'algoliasearch/lite'
import algoliaRouting from '../../components/algolia/routing'
import {Slot} from '../../components/cms/Slot'
import {
  BannerRowSkeleton,
  PLPSkeletonBreadcrumbs,
  PLPSkeletonGrid,
  PLPSkeletonLayout,
  PLPSkeletonStatsFilters,
  PLPSkeletonTiles,
} from '../../components/skeletons'
import {EditOrderStickyBanner} from '../../components/edit-order-sticky-banner'
import {RefinementsDisplay} from '../../components/algolia/instantsearch/refinements-display'
import LoadingOverlay from '../../components/loading-overlay'
import { HITS_PER_PAGE } from '../../constants'
import { useLocation } from 'react-router-dom'
import {isServer} from '../../utils/utils'
import { createCategoryNameString } from '../../utils/retail-media-utils'
import { RetailMediaResponse } from '../../types/retail-media'
import {getBannerSlot, getInGridPromos, getSeo} from '../category/partials/display-slots'
import { usePLPBanners } from '../../store/hooks/usePLPBanners'

interface ContentProps {
  deliveryId?: string
  pageType?: 'CATEGORY' | 'CONTENT'
  categoryId?: string
  serverState: any
  serverUrl: string
  productAds?: RetailMediaResponse
}

const Content: TObservablePage<ContentProps> = observer(
  ({deliveryId, pageType, categoryId, serverState, serverUrl, productAds}) => {
    const contentStore = useContentStore()
    const {fetchAllCategories, status} = useCategoryStore()
    const {status: basketStatus} = useBasketStore()
    const {editMode} = useOrderStore()
    const {
      selectedAvailabilityDayOffset,
      selectedStoreId,
      dataLoadedForAccuratePricingAndAvailabilty,
      isRegionalIndexABTestEnabled
    } = useGlobalStore()

    const [showPageNotFound, setShowPageNotFound] = useState(false)
    const location = useLocation()

    const [filterKey, setFilterKey] = useState<string | null>(null)

    const path = location.pathname
    const route = contentStore.routeMap[path]
    const routeInfo = route?.split('|')
    deliveryId = deliveryId || (routeInfo?.length > 0 ? routeInfo[0] : undefined)
    
    const { topSlot, rowSlots } = usePLPBanners(filterKey, pageType || null)

    const availabilityFilter = `matrixAvailability.${selectedAvailabilityDayOffset}:${selectedStoreId}`
    const inStockFilter = `in_stock:true`
    const defaultLoadingState = pageType === 'CATEGORY' ? false : true
    const [loading, setLoading] = useState(defaultLoadingState)
    
    const getCategories = async () => await fetchAllCategories()
    useEffect(() => {
      if (status === 'idle') {
        getCategories()
      }  
    })

    useEffect(() => {
      if (!deliveryId) {
        setShowPageNotFound(true)
      } else {
        setShowPageNotFound(false)
      }
    }, [dataLoadedForAccuratePricingAndAvailabilty, deliveryId])

    if (showPageNotFound) {
      return (
        <Layout>
          <Seo title="404" />
          <PageNotFound />
        </Layout>
      )
    }

    useEffect(() => {
      if (deliveryId !== undefined && pageType !== undefined && categoryId !== undefined) {
        setLoading(false)
      }
    }, [deliveryId, pageType, categoryId])

    useEffect(() => {
      (async function () {
        if (deliveryId) {
          await flowResult(contentStore.fetchItemById(deliveryId))
          const page = await contentStore.contentById[deliveryId]
          if (pageType === 'CATEGORY' && page?.name) {
            setFilterKey(deliveryId)
          }
        }
    })()
    }, [categoryId, deliveryId])

    if (loading) {
      return (
        <LoadingOverlay isLoading />
      )
    }

    if (!deliveryId) {
      // return a 404 page component
      return (
        <Layout>
          <Seo title="404" />
          <PageNotFound />
        </Layout>
      )
    }

    const page = contentStore.contentById[deliveryId]

    const {topSlot: amplienceBanner} = getBannerSlot(page)
    const bannerSkeleton = amplienceBanner ? <BannerRowSkeleton /> : null

    const inGridPromos = getInGridPromos(page)
    const seo = getSeo(page)

    return (
      <Layout>
        {pageType === 'CATEGORY' && editMode && <EditOrderStickyBanner />}
        {page && <Seo title={seo.pageTitle} description={seo.pageDescription} />}
        <Container
          padding="0px"
          style={page && pageType === 'CATEGORY' ? {minHeight: '800px'} : {}}
        >
          {page && pageType === 'CATEGORY' && (
            <CategoryPage
              slots={page?.slots}
              seo={seo}
              categoryId={categoryId}
              name={page?.name}
              categoryTree={page?.parentCategoryTree}
            >
              {(isServer || basketStatus !== StatusMap.INIT) ? <InstantSearch
                categoryTree={page?.parentCategoryTree}
                seo={seo}
                serverState={serverState}
                serverUrl={serverUrl}
                category={{name: page?.name, id: categoryId || ''}}
                contentAboveHeader={isServer ? bannerSkeleton : topSlot}
                inGridPromos={inGridPromos}
                rowSlots={rowSlots || []}
                presentationAttributes={page.presentationAttributes}
                h2={{
                  textAlignment: page?.pageHeading?.textAlignment?.toLowerCase(),
                  heading2: page?.pageHeading?.heading2,
                }}
                pageName={page?.pageHeading?.heading}
                productAds={productAds}
              >
                <Configure
                /* @TODO this is ignored because of issues with Algolia types. Will be resolved as part of Algolia future updates
                  @ts-ignore */
                  clickAnalytics
                  maxValuesPerFacet={100}
                  filters={`categoryPageId:${categoryId} AND ${inStockFilter} AND NOT ${availabilityFilter}`}
                  hitsPerPage={HITS_PER_PAGE}
                  highlightPostTag='__/ais-highlight__'
                  highlightPreTag='__ais-highlight__'
                  {...(isRegionalIndexABTestEnabled === false && { enableABTest: false })}
                />
              </InstantSearch>
              :
              <PLPSkeletonLayout>
                <PLPSkeletonBreadcrumbs />
                <PLPSkeletonStatsFilters />
                {bannerSkeleton}
                <PLPSkeletonGrid>
                  <PLPSkeletonTiles />
                </PLPSkeletonGrid>
              </PLPSkeletonLayout>}
            </CategoryPage>
          )}
        </Container>
        {page && pageType !== 'CATEGORY' && <Slot data={page} />}
      </Layout>
    )
  },
)

Content.getProps = async ({store, location, res, req}) => {

  await flowResult(store.contentStore.buildRoutingMapAndNavMenu())

  const path = location.pathname
  const route = store.contentStore?.routeMap[path]

  if (route) {
    const [deliveryId, pageType, categoryId] = route.split('|')
    if (isServer) {
      
      /* @TODO this is ignored because of issues with Algolia types. Will be resolved as part of Algolia future updates
        @ts-ignore */
      const searchClient = algoliasearch(process.env.ALGOLIA_APP_ID as string, process.env.ALGOLIA_API_KEY as string, {
        // Add the referer header to all Algolia requests so we can limit API key access
        ...(process.env.APP_ORIGIN !== 'http://localhost:3000') && {
          headers: {
            'Referer': process.env.APP_HOSTNAME
          }
        }
      })
      
      await flowResult(store.contentStore.fetchItemById(deliveryId))
      const page = store.contentStore.contentById[deliveryId]
      const {topSlot} = getBannerSlot(page)
      const bannerSkeleton = topSlot ? <BannerRowSkeleton /> : null
      
      const seo = getSeo(page)

      const protocol = req.headers.referer?.split('://')[0] || 'https'
      const serverUrl = `${protocol}://${req.headers.host}${req.originalUrl}`
      const {
        algoliaIndexBase, 
        selectedAvailabilityDayOffset,
        selectedStoreId,
        isRegionalIndexABTestEnabled
      } = store.globalStore
      const searchIndex = `${algoliaIndexBase}__products__default`
      const routing = algoliaRouting(serverUrl, searchIndex, algoliaIndexBase)
      const availabilityFilter = `matrixAvailability.${selectedAvailabilityDayOffset || 1}:${selectedStoreId || 0}`
      const inStockFilter = `in_stock:true`

      // Fetch product ads on server side for category pages
      let productAds = null
      
      if (pageType === 'CATEGORY' && page?.parentCategoryTree) {
        try {
          // Check if the retailMediaEnabled preference exists, if not, try to load it
          if (!('retailMediaEnabled' in store.globalStore.customSitePreferences)) {
            await flowResult(store.globalStore.getCustomSitePreferenceById('retailMediaEnabled'))
          }
          
          const isRetailMediaEnabled = !!store.globalStore.customSitePreferences?.retailMediaEnabled
          
          if (isRetailMediaEnabled) {
            const categoryNames = page?.parentCategoryTree.map((category: {name: string}) => category.name)
            const completeCategoryString = createCategoryNameString(categoryNames)
            
            productAds = await flowResult(store.retailMediaStore.fetchProductAds('category', completeCategoryString || ''))
          }
        } catch (error) {
          console.error('Error in product ads flow:', error)
          productAds = null
        }
      }

      const serverState =
      pageType === 'CATEGORY'
        ? await getServerState(
            <StaticRouter location={location}>
              <CommerceAPIProvider value={store.api}>
                <StoreProvider value={store}>
                  <IntlProvider locale="en-GB">
                    <Layout>
                      {page && (<Seo title={seo.pageTitle} description={seo.pageDescription} />)}
                      <Container padding="0px">
                        {page && pageType === 'CATEGORY' && (
                          <CategoryPage
                            slots={page?.slots}
                            seo={seo}
                            categoryId={categoryId}
                            name={page?.name}
                            categoryTree={page?.parentCategoryTree}
                          >
                            <AlgoliaInstantSearch
                              searchClient={searchClient}
                              indexName={searchIndex}
                              initialUiState={{
                                [searchIndex]: {query: ''},
                              }}
                              /* @TODO THis is ignored because of issues with Algolia types. Will be resolved as part of Algolia future updates
                              @ts-ignore */
                              routing={routing}
                            >
                              <Configure
                              /* @TODO this is ignored because of issues with Algolia types. Will be resolved as part of Algolia future updates
                                @ts-ignore */
                                clickAnalytics
                                maxValuesPerFacet={100}
                                filters={`categoryPageId:${categoryId} AND ${inStockFilter} AND NOT ${availabilityFilter}`}
                                hitsPerPage={HITS_PER_PAGE}
                                highlightPostTag='__/ais-highlight__'
                                highlightPreTag='__ais-highlight__'
                                tagFilters=''
                                {...(isRegionalIndexABTestEnabled === false && { enableABTest: false })}
                              />
                              <Stack spacing={['24px']} paddingBottom={'20px'}>
                                <ResultsHeader
                                  category={{name: page?.name, id: categoryId || ''
                                }}
                                  content={bannerSkeleton}
                                  presentationAttributes={page.presentationAttributes}
                                  h2={{
                                    textAlignment: page?.pageHeading?.textAlignment?.toLowerCase(),
                                    heading2: page?.pageHeading?.heading2,
                                  }}
                                  pageName={page?.pageHeading?.heading}
                                />
                                <Box aria-hidden borderBottom="1px" borderColor="border0" />
                                <Box display={['none', 'block']}>
                                  <RefinementsDisplay
                                    category={{name: page.name, id: categoryId || ''}}
                                  />
                                </Box>
                                <Results categoryTree={page?.parentCategoryTree} seo={seo} productAds={productAds} />
                                <Pagination />
                              </Stack>
                            </AlgoliaInstantSearch>
                          </CategoryPage>
                        )}
                      </Container>
                      {page && pageType !== 'CATEGORY' && <Slot data={page} />}
                    </Layout>
                  </IntlProvider>
                </StoreProvider>
              </CommerceAPIProvider>
            </StaticRouter>,
            {renderToString},
          )
        : {}


      return {serverState, serverUrl, deliveryId, pageType, categoryId, productAds}
    } else {
      store.contentStore.fetchItemById(deliveryId)
    }

    return {deliveryId, pageType, categoryId}
  } else {
    if (res) {
      res.status(404)
    }
  }
}

export default Content